import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import '@/features/publicsite/contact.css';
import { Icon } from '@/features/common/Icon';
import { RequiredMark } from '@/features/common/RequiredMark';
import { StatusLine } from '@/features/common/StatusLine';
import { useStatusLine } from '@/features/common/useStatusLine';
import { publicSearchApi } from '@/features/publicsite/api/publicSearchApi';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import { publicSearch } from '@/i18n/dictionaries/publicSearch';
import { useTranslation } from '@/i18n/useTranslation';
import { isRejectedAddress } from '@/features/publicsite/contactFailure';

type Field = 'name' | 'email' | 'subject' | 'message';

const EMPTY = { name: '', email: '', subject: '', message: '' };

export function ContactPage() {
  const { t } = useTranslation(publicSearch);
  usePublicPageTitle(t('contactHeading'));
  const privacyId = useId();
  const { status, statusKind, succeed, fail, clear } = useStatusLine();
  // The one live region carries both outcomes, so it cannot be styled by its success colour
  // alone: an error printed in green reads as a message that went through.
  const [emailError, setEmailError] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const emailRef = useRef<HTMLInputElement>(null);

  // A rejected address is reported on the field, and the field is silent until something reads
  // it: the message is not a live region, and aria-describedby is only spoken when focus lands
  // on the input. So the submit that failed sends the reader there. In an effect rather than in
  // the catch, so the message is already in the DOM when the description is read.
  useEffect(() => {
    if (emailError) emailRef.current?.focus();
  }, [emailError]);

  // The ids are literal rather than from useId because public-site.spec.ts fills the form by
  // #name, #email, #subject and #message, and this page renders once per route.
  const field = (key: Field, label: string, placeholder: string, type = 'text') => {
    // Only the address can come back rejected: the endpoint validates it, and a 400 says which
    // field is wrong. Everything else the browser's own required check already holds.
    const invalid = key === 'email' && emailError;
    const errorId = `${key}-error`;
    const describedBy = invalid ? errorId : undefined;
    const shared = {
      id: key,
      name: key,
      required: true,
      placeholder,
      value: form[key],
      'aria-invalid': invalid || undefined,
      'aria-describedby': describedBy,
      onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // Typing into a field the server rejected clears its error: leaving the red border on
        // while the address is being corrected says the correction is wrong too.
        if (invalid) setEmailError(false);
        setForm({ ...form, [key]: e.target.value });
      },
    };

    return (
      <div className="edit-field">
        <label htmlFor={key}>
          {label}
          <RequiredMark />
        </label>
        {type === 'textarea' ? (
          <textarea {...shared} className="textarea" rows={6} />
        ) : (
          <input
            {...shared}
            ref={key === 'email' ? emailRef : undefined}
            className={`text-input${invalid ? ' text-input--destructive' : ''}`}
            type={type}
          />
        )}
        {invalid && (
          <p className="contact-form__error" id={errorId}>
            <Icon name="circle-error" className="contact-form__error-icon" />
            {t('contactInvalid')}
          </p>
        )}
      </div>
    );
  };

  return (
    <section className="container section contact-page">
      <span className="eyebrow">{t('contactEyebrow')}</span>
      <h1 className="contact-page__title">{t('contactHeading')}</h1>
      <p className="contact-page__intro">{t('contactIntro')}</p>

      <form
        className="contact-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setSending(true);
          try {
            await publicSearchApi.sendContact(form);
            setEmailError(false);
            succeed(t('contactSuccess'));
            setForm(EMPTY);
          } catch (err) {
            // A 400 is the endpoint rejecting the address, so it is reported on the field it is
            // about rather than as one more line at the bottom of the form. Anything else is a
            // transport failure that belongs to the whole submission. Either way what was typed
            // stays in place — clearing it would lose the message.
            const rejectedAddress = isRejectedAddress(err);
            setEmailError(rejectedAddress);
            if (rejectedAddress) clear();
            else fail(t('contactError'));
          } finally {
            setSending(false);
          }
        }}
      >
        <div className="contact-form__row">
          {field('name', t('contactName'), t('contactNamePlaceholder'))}
          {field('email', t('contactEmail'), t('contactEmailPlaceholder'), 'email')}
        </div>
        {field('subject', t('contactSubject'), t('contactSubjectPlaceholder'))}
        {field('message', t('contactMessage'), t('contactMessagePlaceholder'), 'textarea')}

        {/* The notice belongs above the button, not below it: informing someone what happens to
            their data after they have already sent it is not informing them. It also describes
            the submit button, so a screen reader reaches it immediately before the action it
            is about rather than only if the reader happens to pass over the paragraph. */}
        <p className="contact-form__notice" id={privacyId}>
          {t('contactPrivacyNotice')}{' '}
          <Link to="/politici/confidentialitate">{t('contactPrivacyLink')}</Link>
        </p>

        <div className="contact-form__actions">
          {/* No size modifier on purpose: the base .btn carries MUD's md metrics (8/16 padding,
              16px), which is the size this form asks for. .btn--md is MUD's *default* size,
              which is larger — that scale inverts md and default. */}
          <button
            className="btn btn--primary"
            type="submit"
            disabled={sending}
            aria-describedby={privacyId}
          >
            {sending ? t('contactSending') : t('contactSend')}
          </button>
          {/* id kept from the template: role="status" alone is ambiguous on this page, because
              PublicShell's language-fallback notice is also a live region. public-site.spec.ts
              addresses this one by id. */}
          <StatusLine
            id="contact-result"
            className="contact-form__result"
            status={status}
            kind={statusKind}
          />
        </div>
      </form>
    </section>
  );
}
